/**
 * Decision-first experiments table (White signal v2).
 * Paper → App → "App v3 · Experiments list · LP-aligned".
 *
 * One row per test, ordered by what needs the user next:
 *   needs decision → running → planned (drafts) → idea → done.
 * Orange marks only the answer: the needs-decision row tint and its winning
 * lift chip. Every other result is a neutral chip or muted text.
 */

import { useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { MoreHorizontalIcon } from "@hugeicons/core-free-icons"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import {
  calculateImprovement,
  determineWinner,
  significanceForGoal,
  type Experiment,
} from "@/hooks/useExperiments"
import type { ExperimentIdea } from "@/lib/utils/experimentIdeas"
import type { ExperimentLogEntry } from "@/lib/utils/experimentLog"
import {
  EXPERIMENT_GOAL_LABELS,
  type ExperimentGoalId,
} from "@/lib/config/experimentContext"
import { isLowDailyTraffic } from "@/lib/utils/abTestStatistics"

export type DecisionDraft = {
  draftId: string
  experimentName: string
  testGoal?: string
  testType?: string
  currentStep?: number
  updatedAt: string
  componentPageUrl?: string
  variants?: Array<{ url?: string }>
}

export type DecisionKind = "decision" | "running" | "planned" | "idea" | "done"

type Result =
  | { type: "answer"; chip: string; note?: string }
  | { type: "chip"; chip: string; note?: string; quiet?: boolean }
  | { type: "progress"; text: string; progress: number }
  | { type: "text"; text: string; tone?: "warning" }

export type DecisionRow = {
  key: string
  kind: DecisionKind
  name: string
  meta: string
  goal: string
  visitors: number | null
  result: Result
  /** Winning lift (%) when a challenger beat control at 95%. */
  winLift?: number
  /** Plain-text next step for running rows ("Ends around Oct 28"). */
  nextText?: string
  sortTime: number
  experiment?: Experiment
  draft?: DecisionDraft
  idea?: ExperimentIdea
}

// ── Formatting helpers ──────────────────────────────────────────────

const DAY_MS = 1000 * 60 * 60 * 24

const shortDate = (value?: string | number | Date | null) => {
  if (!value) return ""
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const displayUrl = (url?: string) => {
  if (!url) return ""
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\?.*$/, "")
}

const signedPct = (value: number) => {
  const rounded = Math.round(Math.abs(value))
  return `${value < 0 ? "−" : "+"}${rounded}%`
}

export function goalLabel(goal?: string): string {
  if (!goal) return "Not set"
  const label =
    EXPERIMENT_GOAL_LABELS[goal as ExperimentGoalId] ?? goal.replace(/_/g, " ")
  // Sentence case: "Form Submissions" → "Form submissions".
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()
}

const variantLetter = (experiment: Experiment, variantId?: string) => {
  const variants = Array.isArray(experiment.variants) ? experiment.variants : []
  const idx = variants.findIndex((v) => v.id === variantId)
  return idx >= 0 ? String.fromCharCode(65 + idx) : "B"
}

// ── Row derivation ──────────────────────────────────────────────────

function experimentRow(
  experiment: Experiment,
  decision: ExperimentLogEntry | undefined,
): DecisionRow {
  const variants = Array.isArray(experiment.variants) ? experiment.variants : []
  const control = variants.find((v) => v.isControl)
  const challengers = variants.filter((v) => !v.isControl)
  const { winner, significance } = determineWinner(variants, 95, experiment.testGoal)
  const calcSig = significanceForGoal(experiment.testGoal)

  // Strongest challenger by significance, so a significant loss is visible too.
  let best: { id: string; sig: number; lift: number } | null = null
  for (const v of challengers) {
    const sig = control
      ? calcSig(control.conversions || 0, control.sessions || 0, v.conversions || 0, v.sessions || 0)
      : 0
    const lift = calculateImprovement(control?.conversionRate || 0, v.conversionRate || 0)
    if (!best || sig > best.sig) best = { id: v.id, sig, lift }
  }
  const confidence = Math.max(significance || 0, best?.sig || 0)

  const winnerLift =
    winner && control
      ? calculateImprovement(control.conversionRate || 0, winner.conversionRate || 0)
      : null
  const controlWon = !winner && !!best && best.sig >= 95 && best.lift < 0

  const visitors = variants.reduce((s, v) => s + (v.sessions || 0), 0)
  const page = displayUrl(control?.url || variants[0]?.url || experiment.finishPageUrl)
  const pageLabel = experiment.propertyDomain && !page ? experiment.propertyDomain : page
  const created = new Date(experiment.createdAt).getTime()
  const ended = experiment.endedAt || experiment.updatedAt
  const dayN = Math.max(1, Math.floor((Date.now() - created) / DAY_MS) + 1)
  const plannedEnd = experiment.plannedEndDate
    ? new Date(experiment.plannedEndDate)
    : experiment.autoStopDurationDays
      ? new Date(created + experiment.autoStopDurationDays * DAY_MS)
      : null

  const base = {
    key: experiment.experimentId,
    name: experiment.experimentName,
    goal: goalLabel(experiment.testGoal),
    visitors: visitors > 0 ? visitors : null,
    winLift: winner && winnerLift != null && winnerLift > 0 ? winnerLift : undefined,
    experiment,
  }
  const join = (...parts: Array<string | false | undefined>) =>
    parts.filter(Boolean).join(" · ")

  const isStopped = experiment.status !== "active"
  const needsDecision = !!winner && winnerLift != null && winnerLift > 0 && !decision &&
    experiment.status !== "archived"

  if (needsDecision) {
    const letter = variantLetter(experiment, winner!.id)
    return {
      ...base,
      kind: "decision",
      meta: join(
        pageLabel,
        isStopped
          ? `stopped at ${Math.round(confidence)}%${ended ? ` on ${shortDate(ended)}` : ""}`
          : `reached ${Math.round(confidence)}% on day ${dayN}`,
      ),
      result: {
        type: "answer",
        chip: `${letter} won ${signedPct(winnerLift!)}`,
        note: isStopped ? "Ready to apply" : "Still running",
      },
      sortTime: new Date(ended || experiment.createdAt).getTime(),
    }
  }

  if (experiment.status === "active") {
    const totalDays = experiment.autoStopDurationDays
    const progress = totalDays
      ? Math.min(1, dayN / totalDays)
      : Math.min(1, confidence / 95)
    // The next step is an action, not the test's age. A test this thin will not
    // reach a result at any length, so say so instead of "Too early to call".
    const perDay = visitors / dayN
    const starved = dayN >= 2 && isLowDailyTraffic(perDay)
    const firstWeekEnds = new Date(created + 7 * DAY_MS)
    const nextText = starved
      ? "Needs traffic"
      : dayN < 7
        ? `Check ${shortDate(firstWeekEnds)}`
        : plannedEnd
          ? `Ends around ${shortDate(plannedEnd)}`
          : "Check daily"
    return {
      ...base,
      kind: "running",
      meta: join(pageLabel, totalDays ? `day ${dayN} of about ${totalDays}` : `day ${dayN}`),
      nextText,
      result: starved
        ? { type: "text", tone: "warning", text: `${perDay < 1 ? "Under 1 visitor" : `${Math.round(perDay)} ${Math.round(perDay) === 1 ? "visitor" : "visitors"}`} a day, too few to call` }
        : { type: "progress", text: dayN < 7 ? "Too early to call · first week" : "Too early to call", progress },
      sortTime: created,
    }
  }

  // Done: completed, paused, archived, or anything with a recorded decision.
  const range = ended
    ? `${shortDate(experiment.createdAt)} to ${shortDate(ended)}`
    : `started ${shortDate(experiment.createdAt)}`
  const applied = decision?.action === "ship" ? `applied ${shortDate(decision.decidedAt)}` : undefined
  let result: Result
  if (winner && winnerLift != null && winnerLift > 0) {
    result = {
      type: "chip",
      chip: `${variantLetter(experiment, winner.id)} won ${signedPct(winnerLift)}`,
      note: decision?.action === "ship" ? "Applied" : decision ? "Not applied" : "Archived",
    }
  } else if (controlWon && best) {
    result = {
      type: "chip",
      chip: `Control won, ${variantLetter(experiment, best.id)} ${signedPct(best.lift)}`,
      note: "Kept Control",
    }
  } else {
    result = {
      type: "chip",
      chip: "No clear difference",
      quiet: true,
      note:
        experiment.status === "archived"
          ? "Archived"
          : experiment.status === "paused" && !decision
            ? "Stopped early"
            : undefined,
    }
  }
  return {
    ...base,
    kind: "done",
    meta: join(pageLabel, range, applied),
    result,
    sortTime: new Date(ended || experiment.createdAt).getTime(),
  }
}

const IDEA_SOURCE: Record<ExperimentIdea["source"], string | undefined> = {
  manual: undefined,
  ai: "suggested by the agent",
  rules: "suggested from your data",
  learning: "from a past test",
}

export function buildDecisionRows({
  experiments,
  drafts,
  ideas,
  log,
}: {
  experiments: Experiment[]
  drafts: DecisionDraft[]
  ideas: ExperimentIdea[]
  log: ExperimentLogEntry[]
}): DecisionRow[] {
  const byId = new Map(log.map((l) => [l.experimentId, l]))
  const rows: DecisionRow[] = experiments.map((e) => experimentRow(e, byId.get(e.experimentId)))

  for (const draft of drafts) {
    const url = displayUrl(draft.componentPageUrl || draft.variants?.[0]?.url)
    const step = Math.min((draft.currentStep ?? 0) + 1, 3)
    rows.push({
      key: `draft:${draft.draftId}`,
      kind: "planned",
      name: draft.experimentName || "Untitled draft",
      meta: [url, `edited ${shortDate(draft.updatedAt)}`].filter(Boolean).join(" · "),
      goal: goalLabel(draft.testGoal),
      visitors: null,
      result: { type: "text", text: `Setup step ${step} of 3` },
      sortTime: new Date(draft.updatedAt).getTime(),
      draft,
    })
  }

  for (const idea of ideas) {
    const source = IDEA_SOURCE[idea.source]
    rows.push({
      key: `idea:${idea.id}`,
      kind: "idea",
      name: idea.title,
      meta: [
        displayUrl(idea.pageUrl),
        `captured ${shortDate(idea.createdAt)}${source ? ` ${source}` : ""}`,
      ]
        .filter(Boolean)
        .join(" · "),
      goal: "Not set",
      visitors: null,
      result: { type: "text", text: "Not planned yet" },
      sortTime: new Date(idea.createdAt).getTime(),
      idea,
    })
  }

  const order: Record<DecisionKind, number> = {
    decision: 0,
    running: 1,
    planned: 2,
    idea: 3,
    done: 4,
  }
  return rows.sort((a, b) => order[a.kind] - order[b.kind] || b.sortTime - a.sortTime)
}

// ── Rendering ───────────────────────────────────────────────────────

const STATUS: Record<DecisionKind, { label: string; dot: string; text: string }> = {
  decision: { label: "Needs decision", dot: "bg-warning", text: "text-foreground" },
  running: { label: "Running", dot: "bg-live", text: "text-foreground" },
  planned: { label: "Planned", dot: "bg-foreground/30", text: "text-foreground/65" },
  idea: { label: "Idea", dot: "ring-1 ring-inset ring-foreground/30", text: "text-foreground/65" },
  done: { label: "Done", dot: "bg-foreground", text: "text-foreground" },
}

/** Column lanes shared by the header and every row. */
const COL = {
  next: "w-[112px] shrink-0",
  status: "w-[124px] shrink-0",
  test: "min-w-0 flex-1 basis-0",
  goal: "w-[140px] shrink-0",
  result: "w-[230px] shrink-0",
  visitors: "w-[72px] shrink-0 text-right",
}

const ROW = "flex items-center gap-4 px-5"

function StepButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className="inline-flex h-7 items-center whitespace-nowrap rounded-lg bg-card px-2.5 text-xs font-medium text-foreground ring-1 ring-foreground/10 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {label}
    </button>
  )
}

export type ExperimentDecisionTableProps = {
  rows: DecisionRow[]
  /** Decided rows shown before "Show N more decided". */
  decidedPreview?: number
  readOnly?: boolean
  onOpenExperiment: (experiment: Experiment) => void
  onContinueDraft: (draft: DecisionDraft) => void
  onDeleteDraft: (draftId: string) => void
  onPlanIdea: (idea: ExperimentIdea) => void
  onDeleteIdea: (idea: ExperimentIdea) => void
  onPauseExperiment: (experiment: Experiment) => void
  onArchiveExperiment: (experiment: Experiment) => void
  onCaptureIdea: () => void
  empty?: React.ReactNode
  /** Hide the "Show more decided / + Capture idea" footer (the no-tests state). */
  hideFooter?: boolean
}

export function ExperimentDecisionTable({
  rows,
  decidedPreview = 3,
  readOnly = false,
  onOpenExperiment,
  onContinueDraft,
  onDeleteDraft,
  onPlanIdea,
  onDeleteIdea,
  onPauseExperiment,
  onArchiveExperiment,
  onCaptureIdea,
  empty,
  hideFooter = false,
}: ExperimentDecisionTableProps) {
  const [showAllDecided, setShowAllDecided] = useState(false)

  const open = rows.filter((r) => r.kind !== "done")
  const done = rows.filter((r) => r.kind === "done")
  // Only collapse decided rows when there are open rows to prioritise.
  const collapse = open.length > 0 && done.length > decidedPreview
  const visibleDone = collapse && !showAllDecided ? done.slice(0, decidedPreview) : done
  const hiddenCount = done.length - visibleDone.length
  const visible = [...open, ...visibleDone]

  const activate = (row: DecisionRow) => {
    if (row.experiment) onOpenExperiment(row.experiment)
    else if (row.draft && !readOnly) onContinueDraft(row.draft)
  }

  return (
    <div className="surface-raised overflow-clip">
      <div className="overflow-x-auto">
        <div className="min-w-[880px]" role="table" aria-label="Experiments">
          {/* Header */}
          <div
            role="row"
            className={cn(ROW, "h-9 border-b border-border bg-muted/50 text-xs text-muted-foreground")}
          >
            <div role="columnheader" className={COL.next}>Next step</div>
            <div role="columnheader" className={COL.status}>Status</div>
            <div role="columnheader" className={COL.test}>Test</div>
            <div role="columnheader" className={COL.goal}>Goal</div>
            <div role="columnheader" className={COL.result}>Result</div>
            <div role="columnheader" className={COL.visitors}>Visitors</div>
          </div>

          {visible.map((row, i) => {
            const status = STATUS[row.kind]
            const clickable = !!row.experiment || (!!row.draft && !readOnly)
            const hasMenu =
              !readOnly && (!!row.draft || !!row.idea || (!!row.experiment && row.experiment.status !== "archived"))
            return (
              <div
                key={row.key}
                role="row"
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => activate(row) : undefined}
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === "Enter") activate(row)
                      }
                    : undefined
                }
                className={cn(
                  ROW,
                  "group relative h-14 transition-colors focus-visible:outline-none focus-visible:bg-muted/60",
                  i > 0 && "border-t border-border/60",
                  row.kind === "decision" ? "bg-primary-tint/50" : clickable && "hover:bg-muted/40",
                  clickable && "cursor-pointer",
                )}
              >
                {/* Next step */}
                <div role="cell" className={cn(COL.next, "flex")}>
                  {row.kind === "decision" && row.experiment ? (
                    <StepButton label="Review" onClick={() => onOpenExperiment(row.experiment!)} />
                  ) : row.kind === "planned" && row.draft && !readOnly ? (
                    <StepButton label="Open editor" onClick={() => onContinueDraft(row.draft!)} />
                  ) : row.kind === "idea" && row.idea && !readOnly ? (
                    <StepButton label="Plan it" onClick={() => onPlanIdea(row.idea!)} />
                  ) : row.kind === "running" ? (
                    <span className="whitespace-nowrap text-xs text-muted-foreground">{row.nextText}</span>
                  ) : (
                    <span className="text-xs text-foreground/40">–</span>
                  )}
                </div>

                {/* Status */}
                <div role="cell" className={cn(COL.status, "flex items-center gap-1.5")}>
                  <span className={cn("size-1.5 shrink-0 rounded-[3px]", status.dot)} aria-hidden />
                  <span className={cn("text-xs font-medium", status.text)}>{status.label}</span>
                </div>

                {/* Test */}
                <div role="cell" className={cn(COL.test, "flex flex-col gap-0.5")}>
                  <span className="truncate text-sm font-medium leading-[18px] text-foreground">
                    {row.name}
                  </span>
                  {row.meta && (
                    <span className="truncate text-xs text-muted-foreground">{row.meta}</span>
                  )}
                </div>

                {/* Goal */}
                <div role="cell" className={cn(COL.goal, "truncate text-[13px] leading-4 text-foreground")}>
                  {row.goal}
                </div>

                {/* Result */}
                <div role="cell" className={COL.result}>
                  <ResultCell result={row.result} />
                </div>

                {/* Visitors */}
                <div
                  role="cell"
                  className={cn(
                    COL.visitors,
                    "text-[13px] leading-4 tabular-nums",
                    row.visitors == null ? "text-foreground/40" : "text-foreground",
                    hasMenu && "transition-opacity group-hover:opacity-0 group-focus-within:opacity-0",
                  )}
                >
                  {row.visitors == null ? "–" : row.visitors.toLocaleString("en-US")}
                </div>

                {hasMenu && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 has-[[data-state=open]]:opacity-100">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          aria-label={`Actions for ${row.name}`}
                          className="inline-flex size-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <HugeiconsIcon icon={MoreHorizontalIcon} size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        {row.experiment && (
                          <>
                            <DropdownMenuItem onClick={() => onOpenExperiment(row.experiment!)}>
                              View details
                            </DropdownMenuItem>
                            {row.experiment.status === "active" && (
                              <DropdownMenuItem onClick={() => onPauseExperiment(row.experiment!)}>
                                Pause test
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => onArchiveExperiment(row.experiment!)}>
                              Archive
                            </DropdownMenuItem>
                          </>
                        )}
                        {row.draft && (
                          <DropdownMenuItem onClick={() => onDeleteDraft(row.draft!.draftId)}>
                            Delete draft
                          </DropdownMenuItem>
                        )}
                        {row.idea && (
                          <DropdownMenuItem onClick={() => onDeleteIdea(row.idea!)}>
                            Delete idea
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
      {/* Outside the 880px scroller so the empty state and footer fit a phone. */}
      {visible.length === 0 && empty}
      {/* Footer */}
      {!hideFooter && (collapse || !readOnly) && (
        <div className="flex h-11 items-center justify-between border-t border-border bg-muted/50 px-5 text-xs font-medium">
          {collapse ? (
            <button
              type="button"
              onClick={() => setShowAllDecided((v) => !v)}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              {showAllDecided ? "Show fewer decided" : `Show ${hiddenCount} more decided`}
            </button>
          ) : (
            <span />
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={onCaptureIdea}
              className="text-foreground transition-colors hover:text-foreground/70"
            >
              + Capture idea
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function ResultCell({ result }: { result: Result }) {
  if (result.type === "text") {
    return <span className={cn("text-xs", result.tone === "warning" ? "text-warning" : "text-muted-foreground")}>{result.text}</span>
  }
  if (result.type === "progress") {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-xs text-muted-foreground">{result.text}</span>
        <div className="h-1 w-[180px] rounded-sm bg-muted">
          <div
            className="h-1 rounded-sm bg-foreground"
            style={{ width: `${Math.round(result.progress * 100)}%` }}
          />
        </div>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          "inline-flex h-[22px] shrink-0 items-center whitespace-nowrap rounded-md px-[7px] text-xs font-medium",
          result.type === "answer"
            ? "bg-primary-tint text-primary-strong ring-1 ring-primary-tint-ring"
            : "bg-muted",
          result.type === "chip" && (result.quiet ? "text-foreground/70" : "text-foreground"),
        )}
      >
        {result.chip}
      </span>
      {result.note && (
        <span className="truncate text-xs text-muted-foreground">{result.note}</span>
      )}
    </div>
  )
}
